import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { Permission } from 'interfaces/permissionOption';
import { filter, find, flatMap, includes, isEmpty, map, size, some, sortBy, uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { getPanelRnaProbeDisplayNames } from 'utils/multiplex/panelChannelPresentation';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import { PanelEditor } from './PanelEditor';
import { createNewPanelDraft, getPanelLabel, NewPanelDraft, PanelChannelDraft } from './panelForm';
import { UnassignedSlides } from './UnassignedSlides';

interface Props {
  casesParams: CasesParams;
  sourceSlideId?: string;
  sourceSlideWithoutCase?: boolean;
  onClose: () => void;
}

export const DefineMultiplexPanelsDialog = ({ casesParams, sourceSlideId, sourceSlideWithoutCase, onClose }: Props) => {
  const studyId = casesParams.studyId || casesParams.filters?.studyId;
  const hasSelection = Boolean(
    size(casesParams.slideIdsToInclude) ||
      size(casesParams.caseIdsToInclude) ||
      size(casesParams.slideIdsToExclude) ||
      size(casesParams.caseIdsToExclude)
  );
  const [scope, setScope] = useState<'selection' | 'study'>(hasSelection && !sourceSlideId ? 'selection' : 'study');
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>({});
  const [dirtyPanels, setDirtyPanels] = useState<Record<string, boolean>>({});
  const [newPanelDrafts, setNewPanelDrafts] = useState<NewPanelDraft[]>([]);
  const [createdPanelIds, setCreatedPanelIds] = useState<string[]>([]);
  const [saveNotice, setSaveNotice] = useState<{ panelId: string; message: (panelLabel: string) => string }>();
  const [pendingScrollPanelId, setPendingScrollPanelId] = useState<string>();
  const panelRefs = useRef(new Map<string, HTMLElement>());
  const confirm = useConfirmation();
  const { hasPermission } = usePermissions();
  const { study } = useStudy(studyId);
  const { stainTypeOptions, isLoadingStainTypeOptions } = useStainTypeOptions();
  const canRead = hasPermission(Permission.EditQCMarkers);
  const canEdit = canRead && hasPermission(Permission.EditStudyNameAndDescription);
  const mutationsPending = useIsMutating({ mutationKey: ['multiplex_panel_mutations', studyId] }) > 0;
  const studyParams: CasesParams = { studyId, labId: casesParams.labId, filters: { studyId }, slidesMode: true };
  const scopedParams =
    scope === 'study' ? studyParams : { ...casesParams, studyId, filters: { ...casesParams.filters, studyId } };
  const query = useQuery({
    queryKey: getMultiplexPanelsQueryKey(scopedParams),
    queryFn: ({ signal }) => getMultiplexPanels(scopedParams, signal),
    enabled: Boolean(studyId) && canRead,
  });
  const unassignedGroups = filter(
    query.data?.unassignedGroups || [],
    (group) => group.slides.length > 0 && (!sourceSlideId || Boolean(find(group.slides, { slideId: sourceSlideId })))
  );
  const candidatePanelIds = flatMap(unassignedGroups, (group) => map(group.compatiblePanels, 'panelId'));
  const persistedPanels = sortBy(
    filter(
      query.data?.panels || [],
      (panel) =>
        !sourceSlideId ||
        includes(candidatePanelIds, panel.panelId) ||
        includes(createdPanelIds, panel.panelId) ||
        Boolean(find([...panel.slides, ...(panel.candidateSlides || [])], { slideId: sourceSlideId }))
    ),
    (panel) => (!panel.saved ? 1 : panel.slides.every((slide) => slide.approvalStatus === 'approved') ? 2 : 0)
  );
  const panels = [...persistedPanels, ...map(newPanelDrafts, 'panel')];
  const allPanels = [...(query.data?.panels || []), ...map(newPanelDrafts, 'panel')];
  const visibleSlides = uniqBy(
    [
      ...flatMap(persistedPanels, 'slides'),
      ...flatMap(persistedPanels, (panel) => panel.candidateSlides || []),
      ...flatMap(unassignedGroups, 'slides'),
    ],
    'slideId'
  );
  const unassignedSlideCount = flatMap(unassignedGroups, 'slides').length;
  const sourceSlideInScope = Boolean(sourceSlideId && find(visibleSlides, { slideId: sourceSlideId }));
  const approvedSlideCount = filter(visibleSlides, { approvalStatus: 'approved' }).length;
  const canDefineNewPanel = newPanelDrafts.length === 0;
  const canDiscard = async () =>
    (!newPanelDrafts.length && !Object.values(dirtyPanels).some(Boolean)) ||
    (await confirm({
      title: 'Discard changes?',
      text: 'Your unsaved panel changes will be discarded.',
      confirmButtonProps: { title: 'Discard changes', color: 'warning', variant: 'contained' },
      cancelButtonProps: { title: 'Keep editing' },
    }));
  const close = async () => {
    if (await canDiscard()) onClose();
  };
  const defineNewPanel = (source: MultiplexPanelView, channels: PanelChannelDraft[], slideIds: string[]) => {
    const draft = createNewPanelDraft(source, channels, slideIds, allPanels);
    setNewPanelDrafts((current) => [...current, draft]);
    setExpandedPanels((current) => ({ ...current, [draft.panel.panelId]: true }));
    setSaveNotice(undefined);
    setPendingScrollPanelId(draft.panel.panelId);
  };
  useEffect(() => {
    if (!pendingScrollPanelId) return;
    panelRefs.current.get(pendingScrollPanelId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setPendingScrollPanelId(undefined);
  }, [pendingScrollPanelId]);

  return (
    <Dialog open maxWidth="lg" fullWidth onClose={close} aria-labelledby="multiplex-panel-setup-title">
      <DialogChromeHeader
        title="Define multiplex panels"
        titleId="multiplex-panel-setup-title"
        onClose={close}
        sx={{ '& .MuiTypography-h1': { fontSize: 20, lineHeight: 1.35 } }}
      />
      <DialogContent dividers sx={{ minWidth: 0, overflowX: 'hidden' }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {!studyId ? (
            <Alert severity="warning">Select a study to configure its multiplex panels.</Alert>
          ) : !canRead ? (
            <Alert severity="info">Marker editing permission is required to inspect panels.</Alert>
          ) : (
            <>
              {hasSelection && !sourceSlideId && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={scope}
                  disabled={mutationsPending}
                  aria-label="Slides to analyze"
                  onChange={async (_event, value) => {
                    if (value && value !== scope && (await canDiscard())) {
                      setScope(value);
                      setDirtyPanels({});
                      setNewPanelDrafts([]);
                      setCreatedPanelIds([]);
                      setSaveNotice(undefined);
                      setExpandedPanels({});
                    }
                  }}
                >
                  <ToggleButton value="selection">Selected slides</ToggleButton>
                  <ToggleButton value="study">All multiplex slides in study</ToggleButton>
                </ToggleButtonGroup>
              )}
              {sourceSlideInScope && (
                <Alert severity="info">
                  Changes apply only to selected slides. This slide is already selected; open a panel’s slide list to
                  select other slides with the same channels.
                </Alert>
              )}
              {query.isLoading || isLoadingStainTypeOptions ? (
                <Stack spacing={1}>
                  <Skeleton variant="rectangular" height={180} />
                  <Typography variant="body2" color="text.secondary" role="status">
                    Loading panel information across the cohort…
                  </Typography>
                </Stack>
              ) : query.isError ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" disabled={query.isFetching} onClick={() => query.refetch()}>
                      Retry
                    </Button>
                  }
                >
                  Panel information could not be loaded. Retry to refresh the current settings.
                </Alert>
              ) : (
                query.data && (
                  <>
                    {saveNotice && (
                      <Alert severity="success">
                        {saveNotice.message(getPanelLabel(saveNotice.panelId, allPanels))}
                      </Alert>
                    )}
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip size="small" label={`${panels.length} ${panels.length === 1 ? 'panel' : 'panels'} found`} />
                      <Chip
                        size="small"
                        color={approvedSlideCount ? 'success' : 'default'}
                        label={`${approvedSlideCount} of ${visibleSlides.length} ${
                          sourceSlideId ? 'matching ' : ''
                        }slides approved`}
                      />
                      <Chip size="small" label={`${filter(panels, (panel) => !panel.saved).length} new panels`} />
                      {unassignedSlideCount > 0 && (
                        <Chip
                          size="small"
                          label={`${unassignedSlideCount} unassigned ${
                            unassignedSlideCount === 1 ? 'slide' : 'slides'
                          }`}
                        />
                      )}
                    </Stack>
                    {some(unassignedGroups, (group) => !group.compatiblePanels.length) && (
                      <Stack spacing={2} aria-label="Slides without a compatible panel">
                        <Typography variant="subtitle1">Slides without a compatible panel</Typography>
                        {map(
                          filter(unassignedGroups, (group) => !group.compatiblePanels.length),
                          (group) => (
                            <UnassignedSlides
                              key={`${scope}:${group.groupId}`}
                              group={group}
                              panels={query.data?.panels || []}
                              studyId={studyId}
                              labId={casesParams.labId}
                              stains={stainTypeOptions}
                              canEdit={canEdit}
                              sourceSlideId={sourceSlideId}
                              refreshing={query.isFetching}
                              onDefineNewPanel={canDefineNewPanel ? defineNewPanel : undefined}
                              onAssociated={(panelId, requiresReanalysis) => {
                                setExpandedPanels((current) => ({ ...current, [panelId]: true }));
                                setSaveNotice({
                                  panelId,
                                  message: (panelLabel) =>
                                    `Slides associated with ${panelLabel}. ${
                                      requiresReanalysis
                                        ? 'Panel-dependent analyses need re-analysis. Initial image analysis is retained.'
                                        : 'Existing channel settings are unchanged.'
                                    }`,
                                });
                              }}
                            />
                          )
                        )}
                      </Stack>
                    )}
                    {isEmpty(panels) && !unassignedGroups.length ? (
                      sourceSlideWithoutCase ? (
                        <Alert severity="info">
                          This slide is not in a case in this study, so it cannot be assigned to a panel yet. Add it to
                          a case, then define its panel here.
                        </Alert>
                      ) : (
                        <Alert severity="info">
                          No multiplex channel metadata is available for this cohort. Finish slide ingestion, then
                          define its panels.
                        </Alert>
                      )
                    ) : (
                      map(panels, (panel) => (
                        <Box
                          key={`${scope}:${panel.panelId}`}
                          ref={(element: HTMLElement | null) => {
                            if (element) panelRefs.current.set(panel.panelId, element);
                            else panelRefs.current.delete(panel.panelId);
                          }}
                        >
                          <PanelEditor
                            panel={panel}
                            panels={allPanels}
                            usersById={query.data?.usersById}
                            studyId={studyId}
                            labId={casesParams.labId}
                            stains={stainTypeOptions}
                            displayNames={getPanelRnaProbeDisplayNames(study?.settings?.multiplexPanels)}
                            panelRevisions={study?.settings?.multiplexPanels || []}
                            initialChannels={
                              find(newPanelDrafts, (draft) => draft.panel.panelId === panel.panelId)?.channels
                            }
                            isNewPanelDraft={Boolean(
                              find(newPanelDrafts, (draft) => draft.panel.panelId === panel.panelId)
                            )}
                            onDefineNewPanel={
                              canDefineNewPanel
                                ? (channels, slideIds) => defineNewPanel(panel, channels, slideIds)
                                : undefined
                            }
                            onDiscardDraft={
                              find(newPanelDrafts, (draft) => draft.panel.panelId === panel.panelId)
                                ? () => {
                                    setNewPanelDrafts((current) =>
                                      filter(current, (draft) => draft.panel.panelId !== panel.panelId)
                                    );
                                    setDirtyPanels((current) => ({ ...current, [panel.panelId]: false }));
                                    setExpandedPanels((current) => ({ ...current, [panel.panelId]: false }));
                                  }
                                : undefined
                            }
                            onSaved={(revision, requiresReanalysis) => {
                              setNewPanelDrafts((current) =>
                                filter(current, (draft) => draft.panel.panelId !== panel.panelId)
                              );
                              setCreatedPanelIds((current) => [...current, revision.panelId]);
                              setExpandedPanels((current) => ({ ...current, [revision.panelId]: true }));
                              setSaveNotice({
                                panelId: revision.panelId,
                                message: (panelLabel) =>
                                  `${panelLabel} saved. ${
                                    requiresReanalysis
                                      ? 'Panel-dependent analyses need re-analysis. Initial image analysis and historical results are retained.'
                                      : 'Display names updated; re-analysis is not required.'
                                  }`,
                              });
                            }}
                            sourceSlideId={sourceSlideId}
                            initialSlideIds={sourceSlideId ? [sourceSlideId] : undefined}
                            canEdit={canEdit}
                            refreshing={query.isFetching}
                            onAssociated={(requiresReanalysis) =>
                              setSaveNotice({
                                panelId: panel.panelId,
                                message: (panelLabel) =>
                                  `${panelLabel} applied to selected slides. ${
                                    requiresReanalysis
                                      ? 'Panel-dependent analyses need re-analysis. Initial image analysis is retained.'
                                      : 'Existing marker settings are unchanged.'
                                  }`,
                              })
                            }
                            expanded={expandedPanels[panel.panelId] ?? panel.panelId === panels[0].panelId}
                            onExpand={() =>
                              setExpandedPanels((current) => ({
                                ...current,
                                [panel.panelId]: !(current[panel.panelId] ?? panel.panelId === panels[0].panelId),
                              }))
                            }
                            onDirtyChange={(panelId, dirty) =>
                              setDirtyPanels((current) => ({ ...current, [panelId]: dirty }))
                            }
                          />
                        </Box>
                      ))
                    )}
                  </>
                )
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        <Button variant="outlined" color="inherit" onClick={close}>
          Close
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
